import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  LockKeyhole,
  Camera,
  ScrollText,
  Receipt as ReceiptIcon,
  Banknote,
  FileSpreadsheet,
  TrendingUp,
  Printer,
  Link as LinkIcon,
  CloudUpload,
  BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import { BillyLogo, BillyMark } from "@/components/BillyLogo";

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const FEATURES = [
  {
    icon: Camera,
    title: "Smart Capture",
    body:
      "Snap or upload anything — photos, multiple files, or PDFs. Billy reads it with AI and figures out if it's a bill, receipt, bank transaction or quote, then fills the right form for you.",
  },
  {
    icon: ScrollText,
    title: "Bills & Reminders",
    body:
      "Track every bill from unpaid → part-paid → paid. Partial payments, recurring schedules, full payment history and overdue alerts — no spreadsheet needed.",
  },
  {
    icon: ReceiptIcon,
    title: "Receipts for BAS",
    body:
      "Snap till receipts, fuel, tools, subbies. Auto-categorised. One-click CSV or PDF export for any quarter — your accountant will love you.",
  },
  {
    icon: Banknote,
    title: "Bank-Transaction Allocation",
    body:
      "Upload a bank screenshot. Billy matches the payment to the right outstanding bill and lets you split it across multiple bills with one tap.",
  },
  {
    icon: FileSpreadsheet,
    title: "Quote → 3 Deposits",
    body:
      "Upload a quote and Billy creates three Income entries automatically — 50% on signing, 30% on install, 20% on completion.",
  },
  {
    icon: TrendingUp,
    title: "Weekly Cash-Flow",
    body:
      "See this week's bills, expected income and profit target at a glance. Editable weekly goal with an 8-week trend chart.",
  },
  {
    icon: Printer,
    title: "Bank-Ready Forecast",
    body:
      "One click prints a 3-month cash-flow forecast (3 months actual + 3 projected, with overdue) — exactly the document a loan officer asks for.",
  },
  {
    icon: LinkIcon,
    title: "Public Pay Links",
    body:
      "Every bill gets a /pay link you can text to the customer. They tap once, mark it paid — and it shows up live in your dashboard.",
  },
];

const LoginPage = () => {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const next = new URLSearchParams(location.search).get("next") || "/";

  useEffect(() => {
    if (!loading && user) navigate(next, { replace: true });
  }, [loading, user, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate(next, { replace: true });
    } catch (err) {
      toast.error(
        formatErr(err?.response?.data?.detail) || "Login failed. Try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-12 lg:gap-20 items-start">
        {/* Left — pitch */}
        <div data-testid="login-pitch" className="fade-rise">
          <BillyLogo size={56} tone="dark" />

          <div className="swiss-label mt-10 mb-3">Bill reminders. Reinvented.</div>
          <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-[1.02]">
            Snap. Send. <span className="text-red-600">Paid.</span>
          </h1>
          <p className="text-neutral-700 text-lg mt-4 max-w-2xl">
            Billy is the mobile-first bill & cash-flow command centre for The
            Outdoor Blind Company. Bills, receipts, quotes, bank transactions —
            all in one place, read by AI, kept in the cloud, and ready to print
            when the bank wants a forecast.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <div className="border-2 border-black px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-2">
              <CloudUpload className="h-3.5 w-3.5" /> Cloud-saved
            </div>
            <div className="border-2 border-black px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold inline-flex items-center gap-2">
              <Camera className="h-3.5 w-3.5" /> AI reads everything
            </div>
            <div className="border-2 border-black px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold">
              Built for Aussie tradies
            </div>
            <div className="border-2 border-black px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-bold">
              BAS / GST ready
            </div>
          </div>

          {/* Feature grid */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="login-features">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="border-2 border-black bg-white p-4 transition-transform hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <div className="font-bold uppercase tracking-widest text-[11px]">
                    {title}
                  </div>
                </div>
                <p className="text-sm text-neutral-700 mt-2 leading-snug">
                  {body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
            Want the full tour?{" "}
            <Link
              to="/guide"
              data-testid="open-guide-link"
              className="text-black border-b-2 border-black hover:bg-black hover:text-white inline-flex items-center gap-1 px-1"
            >
              <BookOpen className="h-3 w-3" />
              Open the User Guide
            </Link>
          </div>
        </div>

        {/* Right — sign-in card */}
        <div
          data-testid="login-page"
          className="bg-white border-2 border-black p-8 md:p-10 fade-rise lg:sticky lg:top-10"
        >
          <div className="flex items-center gap-3 mb-6">
            <BillyMark size={36} />
            <div>
              <div className="swiss-label">Sign in</div>
              <div className="font-display font-black text-xl leading-none mt-1">
                Welcome back
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <div className="swiss-label mb-1">Email</div>
              <Input
                data-testid="input-email"
                className="swiss-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder="craig@theoutdoorblindcompany.com"
                required
              />
            </label>
            <label className="block">
              <div className="swiss-label mb-1">Password</div>
              <Input
                data-testid="input-password"
                className="swiss-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                autoComplete="current-password"
                required
              />
            </label>

            <button
              type="submit"
              data-testid="login-btn"
              disabled={busy}
              className="w-full border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-6 py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LockKeyhole className="h-4 w-4" />
              )}
              Sign in
            </button>
          </form>

          <div className="mt-8 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            Authorised users only · The Outdoor Blind Company
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
